import { useCallback, useState } from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';

import {
  Certificate,
  deleteCertificate,
  errorMessageFrom,
  isConflict,
} from './api';

type Props = {
  certificate: Certificate;
  onClose: () => void;
  onDeleted: () => void;
};

const DeleteBadgeModal = ({ certificate, onClose, onDeleted }: Props) => {
  const [error, setError] = useState<string | null>(null);
  const [needsDetach, setNeedsDetach] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { usage } = certificate;
  const inUse = usage.customers > 0 || usage.upcoming_jobs > 0;

  const handleDelete = useCallback(
    async (detach: boolean) => {
      setIsLoading(true);
      setError(null);
      try {
        await deleteCertificate(certificate.id, detach);
        onDeleted();
        onClose();
      } catch (err: any) {
        // 409 means it is still required somewhere; offer the detach path
        // rather than leaving the admin stuck.
        if (isConflict(err)) setNeedsDetach(true);
        setError(errorMessageFrom(err, 'Failed to delete badge.'));
      } finally {
        setIsLoading(false);
      }
    },
    [certificate.id, onClose, onDeleted],
  );

  const showDetach = needsDetach || inUse;

  return (
    <Modal
      disableClickout
      size="xs"
      title={`Delete ${certificate.name}?`}
      onRequestClose={onClose}
    >
      <Card.Section>
        <Stack vertical gap={16}>
          <Body>
            This hides the badge from the client and order pickers. Workers who
            already hold it keep it.
          </Body>

          {usage.workers > 0 && (
            <Small color="light">
              {usage.workers} worker{usage.workers === 1 ? '' : 's'} currently
              hold this badge.
            </Small>
          )}

          {showDetach && (
            <Stack vertical gap={8}>
              <Body color="danger" weight="semibold">
                Still in use
              </Body>
              <Small>
                {usage.customers > 0 &&
                  `${usage.customers} client${
                    usage.customers === 1 ? '' : 's'
                  } require it. `}
                {usage.upcoming_jobs > 0 &&
                  `${usage.upcoming_jobs} upcoming job${
                    usage.upcoming_jobs === 1 ? '' : 's'
                  } require it. `}
                A deleted badge still blocks workers from jobs that require it,
                while no longer being visible to remove. Choose “Delete and
                detach” to remove it from those clients and upcoming jobs too.
              </Small>
            </Stack>
          )}

          {error && !showDetach && <Body color="danger">{error}</Body>}

          <Stack justify="end" gap={8}>
            <Button
              a11yLabel="Cancel"
              appearance="outline"
              label="Cancel"
              onClick={onClose}
            />
            {showDetach ? (
              <Button
                a11yLabel="Delete and detach badge"
                isLoading={isLoading}
                label="Delete and detach"
                status="danger"
                onClick={() => handleDelete(true)}
              />
            ) : (
              <Button
                a11yLabel="Delete badge"
                isLoading={isLoading}
                label="Delete"
                status="danger"
                onClick={() => handleDelete(false)}
              />
            )}
          </Stack>
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default DeleteBadgeModal;
